import { isEmpty, isNil } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { Dropdown, DropdownButton, DropdownListItem } from '@shared/components/dropdown';
import { List } from '@shared/components/filter/List';
import { TagOptions } from '@shared/components/filter/filterOptions/TagOptions';
import { ClosedView } from '@shared/components/filter/filterOptions/multiSelect/MultiSelect';
import { mapFilterByComponentToState } from '@shared/components/filter/state/mapCollectionCriteriaToState';
import {
  ControlOption,
  FilterOperation,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
  FilterTagSelection,
} from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput/MBTextInput';
import {
  MBTextBody1,
  MBTextBody1Bold,
  MBTextBody2,
} from '@shared/components/typography/MBTypography';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';

import { Colors } from '@shared/styles/colors';
import { grid } from '@shared/styles/grid';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import styles from './BatchScheduleDesktop.module.css';

interface OffsetOptionsProps {
  offset: MBAPI.CreateRecurrenceOffset | null;
  setOffset: (offset: MBAPI.CreateRecurrenceOffset | null) => void;
  tagFilter: MBAPI.FilterByComponent | undefined;
  setTagFilter: (tagFilter: MBAPI.FilterByComponent | undefined) => void;
  workflowTemplateId: string;
}

export function OffsetOptions({
  offset,
  setOffset,
  tagFilter,
  setTagFilter,
  workflowTemplateId,
}: OffsetOptionsProps) {
  const updateOffset = (key: keyof MBAPI.CreateRecurrenceOffset, value: any) => {
    setOffset({
      ...(offset ?? { value: 1, unit: 'DAY' }),
      [key]: value,
    });
  };

  const hasTagFilter = !isNil(tagFilter) && !isEmpty(tagFilter.value?.containsString?.values);

  return (
    <div style={{ paddingLeft: grid(2), paddingRight: grid(2) }}>
      <TagFilter
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        workflowTemplateId={workflowTemplateId}
      />
      {hasTagFilter && (
        <>
          <div style={{ marginBottom: grid(1), marginTop: grid(2) }}>
            <MBTextBody1Bold>
              {i18n('workflowTemplate.component.dateAndRepeat.offset.createAnotherCardIn')}
            </MBTextBody1Bold>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <MBTextInput
              value={String(offset?.value ?? '')}
              onChangeText={val => updateOffset('value', Number(val))}
              style={{
                width: grid(5.5),
                borderColor: Colors.v2.gray10,
                marginRight: grid(1.5),
              }}
            />
            <FrequencyMenu
              unit={offset?.unit}
              setUnit={val => updateOffset('unit', val)}
              value={offset?.value}
            />
          </div>
        </>
      )}
    </div>
  );
}

const unitLabels: Record<MBAPI.RecurrenceOffsetUnit, { singular: string; plural: string }> = {
  DAY: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.plural'),
  },
  WEEK: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.plural'),
  },
  MONTH: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.plural'),
  },
  YEAR: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.plural'),
  },
};

function FrequencyMenu({
  unit,
  setUnit,
  value,
}: {
  unit: MBAPI.RecurrenceOffsetUnit | undefined;
  setUnit: (unit: MBAPI.RecurrenceOffsetUnit) => void;
  value: number | undefined;
}) {
  const units: MBAPI.RecurrenceOffsetUnit[] = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
  const isSingular = value === 1;
  const label = isSingular ? unitLabels[unit ?? 'DAY'].singular : unitLabels[unit ?? 'DAY'].plural;

  return (
    <Dropdown
      button={({ onClick, isOpen }) => (
        <DropdownButton onClick={onClick} open={isOpen} style={{ flexGrow: 1 }}>
          <MBTextBody1>{label}</MBTextBody1>
        </DropdownButton>
      )}
      closeOnSelect
      className={styles.frequencyMenu}
    >
      {units.map(u => (
        <DropdownListItem key={u} onPress={() => setUnit(u)} className={styles.item}>
          {unitLabels[u].plural}
        </DropdownListItem>
      ))}
    </Dropdown>
  );
}

export function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  maxTagDropdownWidth = 220,
}: {
  tagFilter?: MBAPI.FilterByComponent;
  setTagFilter: (tagFilter?: MBAPI.FilterByComponent) => void;
  workflowTemplateId: string;
  maxTagDropdownWidth?: number;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(component => ({
    id: component.id,
    label: component.label ?? '',
    icon: component.icon as MBIconV2Name,
  }));
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === tagFilter?.componentId,
  );
  const selectedTagComponent = tagComponents?.find(c => c.id === tagFilter?.componentId);

  const isMultiSelect = selectedTagComponent?.multiselect;

  const filterUuidRef = useRef<string>(uuidv4());
  const [componentDropdownKey, setComponentDropdownKey] = useState(0);
  const [tagOptionsDropdownKey, setTagOptionsDropdownKey] = useState(0);

  // Validation state for component dropdown (tag component selection)
  const [componentDropdownError, setComponentDropdownError] = useState(false);
  const componentDropdownOpenedRef = useRef(false);
  const componentDropdownWasOpenRef = useRef(false);

  // Validation state for tagOptions dropdown (tag value selection)
  const [tagOptionsDropdownError, setTagOptionsDropdownError] = useState(false);
  const tagOptionsDropdownOpenedRef = useRef(false);
  const tagOptionsDropdownWasOpenRef = useRef(false);

  const handleComponentDropdownOpenChange = (isOpen: boolean) => {
    if (isOpen && !componentDropdownWasOpenRef.current) {
      // Dropdown just opened
      componentDropdownOpenedRef.current = true;
    } else if (!isOpen && componentDropdownWasOpenRef.current) {
      // Dropdown just closed
      if (componentDropdownOpenedRef.current && isNil(selectedTagComponentOption)) {
        setComponentDropdownError(true);
      }
    }
    componentDropdownWasOpenRef.current = isOpen;
  };

  const handleTagOptionsDropdownOpenChange = (isOpen: boolean) => {
    if (isOpen && !tagOptionsDropdownWasOpenRef.current) {
      // Dropdown just opened
      tagOptionsDropdownOpenedRef.current = true;
    } else if (!isOpen && tagOptionsDropdownWasOpenRef.current) {
      // Dropdown just closed
      if (
        tagOptionsDropdownOpenedRef.current &&
        (isNil(currentSelections) || currentSelections.length === 0)
      ) {
        setTagOptionsDropdownError(true);
      }
    }
    tagOptionsDropdownWasOpenRef.current = isOpen;
  };

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }
    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label,
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    } as ControlOption;
  }, [selectedTagComponent]);

  const selectedOptions = useMemo(() => {
    if (!filterOption) {
      return new Map<string, FilterSelectionState>();
    }

    const hasTagValues = Boolean(tagFilter?.value?.containsString?.values?.length);

    if (!hasTagValues) {
      return new Map<string, FilterSelectionState>();
    }

    const filterArray = tagFilter ? [tagFilter] : [];
    return mapFilterByComponentToState(filterArray, [filterOption]);
  }, [tagFilter, filterOption]);

  const handleTagComponentOptionChange = (option: ControlOption) => {
    // Clear error when a selection is made
    setComponentDropdownError(false);
    // Reset the open state tracking since we're programmatically closing
    componentDropdownOpenedRef.current = false;
    componentDropdownWasOpenRef.current = false;
    // Also reset tagOptions dropdown state since we're changing the component
    setTagOptionsDropdownError(false);
    tagOptionsDropdownOpenedRef.current = false;
    tagOptionsDropdownWasOpenRef.current = false;

    // If the user selects the same tag component that is already selected, deselect it.
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    const newFilter: MBAPI.FilterByComponent = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as MBAPI.FilterByComponent;

    setTagFilter(newFilter);

    // Close dropdown on select (defer to allow state to update before remounting)
    setComponentDropdownKey(prev => prev + 1);
  };

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !selectedTagComponent) {
      return;
    }

    const selections = (selection as FilterTagOption[])?.filter(notNil) ?? [];

    // If all selections are cleared, reset the tag filter values
    if (isEmpty(selections)) {
      setTagFilter({
        componentId: selectedTagComponent.id,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as MBAPI.FilterByComponent);
      return;
    }

    // Clear error when a selection is made
    setTagOptionsDropdownError(false);

    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: selections as unknown as FilterSelection,
      negate: true,
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }

    // Close dropdown on select for single-select (not on deselect/clear)
    if (!isMultiSelect) {
      // Reset the open state tracking since we're programmatically closing
      tagOptionsDropdownOpenedRef.current = false;
      tagOptionsDropdownWasOpenRef.current = false;
      setTagOptionsDropdownKey(prev => prev + 1);
    }
  };

  const currentSelections = useMemo(() => {
    if (selectedOptions.size === 0) {
      return undefined;
    }
    return Array.from(selectedOptions.values())[0]?.selection as FilterTagSelection[];
  }, [selectedOptions]);

  const ItemComponent = ({ selection }: { selection: FilterTagOption }) => {
    if (!selection?.label) {
      return null;
    }
    return (
      <Tag
        color={selection.backgroundColor}
        label={selection.label}
        archived={selection.archived}
        style={{ maxWidth: grid(24) }}
      />
    );
  };

  return (
    <View>
      <div style={{ marginBottom: grid(1), marginTop: grid(2) }}>
        <MBTextBody1Bold>
          {i18n('workflowTemplate.component.dateAndRepeat.offset.whenTheField')}
        </MBTextBody1Bold>
      </div>
      {/* Tag component selection */}
      <View row centerVertically flex>
        <View flex>
          <Dropdown
            key={componentDropdownKey}
            button={({ onClick, isOpen }) => {
              handleComponentDropdownOpenChange(isOpen);
              return (
                <DropdownButton onClick={onClick} open={isOpen}>
                  <View row centerVertically style={{ maxWidth: '78%' }}>
                    {selectedTagComponentOption?.icon && (
                      <MBIconV2
                        name={selectedTagComponentOption.icon as MBIconV2Name}
                        color={Colors.v2.purple}
                        size={grid(3)}
                        style={{ marginRight: grid(1) }}
                      />
                    )}
                    <MBTextBody1
                      numberOfLines={1}
                      color={
                        !isNil(selectedTagComponentOption) ? Colors.v2.purple : Colors.v2.gray50
                      }
                    >
                      {selectedTagComponentOption?.label ||
                        i18n('card.actions.groups.noneSelected')}
                    </MBTextBody1>
                  </View>
                </DropdownButton>
              );
            }}
          >
            <List
              options={tagComponentOptions}
              selected={selectedTagComponentOption}
              onSelect={handleTagComponentOptionChange}
            />
          </Dropdown>
        </View>
      </View>
      {componentDropdownError && (
        <div style={{ marginTop: grid(0.5) }}>
          <MBTextBody2 color={Colors.v2.red}>{i18n('card.form.errors.required')}</MBTextBody2>
        </div>
      )}
      {/* Tag value selection */}
      {filterOption && (
        <>
          <div style={{ marginBottom: grid(1), marginTop: grid(2) }}>
            <MBTextBody1Bold>
              {(isNil(currentSelections) || currentSelections.length <= 1) &&
                i18n('workflowTemplate.component.dateAndRepeat.offset.updatesTo')}
              {!isNil(currentSelections) &&
                currentSelections.length > 1 &&
                i18n('workflowTemplate.component.dateAndRepeat.offset.updatesToOneOf')}
            </MBTextBody1Bold>
          </div>
          <Dropdown
            key={`${filterOption.id}-${tagOptionsDropdownKey}`}
            button={({ onClick, isOpen }) => {
              handleTagOptionsDropdownOpenChange(isOpen);
              return (
                <DropdownButton onClick={onClick} open={isOpen} style={{ paddingLeft: 0 }}>
                  <ClosedView
                    selections={(currentSelections as FilterTagOption[]) || []}
                    ItemComponent={ItemComponent}
                    maxWidth={maxTagDropdownWidth}
                  />
                </DropdownButton>
              );
            }}
            style={{ minWidth: 300 }}
          >
            <TagOptions
              filter={filterOption}
              setOption={handleSetOption}
              selectedOptions={selectedOptions}
              includeEmptyOption={false}
              isMultiSelect={!!isMultiSelect}
            />
          </Dropdown>
          {tagOptionsDropdownError && (
            <div style={{ marginTop: grid(0.5) }}>
              <MBTextBody2 color={Colors.v2.red}>{i18n('card.form.errors.required')}</MBTextBody2>
            </div>
          )}
        </>
      )}
    </View>
  );
}
